import {
  verifyMessageSignaturesStrict,
} from './verification';
import type { MessageBase, Signature, Validateurs } from '../types/message';

function isValidateursShape(v: unknown): v is Validateurs {
  return (
    typeof v === 'object' &&
    v !== null &&
    'membres_du_role' in v &&
    Array.isArray((v as Validateurs).membres_du_role)
  );
}

/**
 * Return validateurs if message is MessageAValider, else null.
 */
export function getValidateursIfMessageAValider(
  msg: MessageBase,
): Validateurs | null {
  const v = (msg as { validateurs?: unknown }).validateurs;
  return isValidateursShape(v) ? v : null;
}

/**
 * Build allowed pubkeys from validateurs (cle_publique in signatures_obligatoires).
 * Used when accepting a version of an object during sync; no pair resolution.
 */
export function buildAllowedPubkeysFromValidateurs(
  validateurs: Validateurs,
): Set<string> {
  const out = new Set<string>();
  for (const m of validateurs.membres_du_role) {
    for (const r of m.signatures_obligatoires) {
      if (r.cle_publique !== undefined) {
        out.add(r.cle_publique);
      }
    }
  }
  return out;
}

/**
 * Check if we can verify validators from validateurs (at least one cle_publique).
 */
export function canVerifyValidateurs(validateurs: Validateurs): boolean {
  return buildAllowedPubkeysFromValidateurs(validateurs).size > 0;
}

export interface ValidateSignaturesResult {
  accept: boolean;
  reason?: string;
}

/**
 * Verify that signatures satisfy validateurs (allowed pubkeys, no unauthorized).
 * Accept only if all signers are allowed and at least one valid signature.
 * Used when accepting a version of an object (sync); cardinality/deps require
 * pair resolution and are not enforced here.
 */
export function validateSignaturesAgainstValidateurs(
  message: MessageBase,
  signatures: Signature[],
  validateurs: Validateurs,
): ValidateSignaturesResult {
  const allowed = buildAllowedPubkeysFromValidateurs(validateurs);
  if (allowed.size === 0) {
    return {
      accept: false,
      reason: 'validators_not_verifiable_no_cle_publique',
    };
  }
  const { valid, unauthorized } = verifyMessageSignaturesStrict(
    message,
    signatures,
    allowed,
  );
  if (unauthorized.length > 0) {
    return {
      accept: false,
      reason: 'signature_cle_publique_not_authorized',
    };
  }
  if (valid.length === 0) {
    return {
      accept: false,
      reason: 'no_validator_signature',
    };
  }
  return { accept: true };
}

/**
 * Full check for sync: validators verifiable, sigs present, all allowed, ≥1 valid.
 * Logs warnings on failure. Used by SyncService.
 */
export function validateMessageAValiderForSync(
  msg: MessageBase,
  sigList: Signature[],
  validateurs: Validateurs,
  logPrefix: string,
): boolean {
  if (!canVerifyValidateurs(validateurs)) {
    console.warn(
      `${logPrefix} MessageAValider: no cle_publique in validateurs, skip`,
    );
    return false;
  }
  if (sigList.length === 0) {
    return false;
  }
  const result = validateSignaturesAgainstValidateurs(
    msg,
    sigList,
    validateurs,
  );
  if (!result.accept) {
    if (result.reason !== undefined) {
      console.warn(`${logPrefix} MessageAValider validation failed: ${result.reason}`);
    }
    return false;
  }
  return true;
}
